import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import FieldDictate from "./FieldDictate";
import { Textarea } from "./ui/textarea";
import { History, CheckSquare, Square, Target } from "lucide-react";

export const GUIDED_QUESTIONS = [
  { key: "did", label: "What did you actually do with them this week?", hint: "Tools you used, prompts you gave, skills you taught, how many times. Past tense — what happened, not the plan.", tag: "WHAT STAFF DID THIS WEEK" },
  { key: "response", label: "How did they respond? What could they do?", hint: "Participation, what they did on their own vs. with a prompt, anything they said.", tag: "MEMBER RESPONSE / PROGRESS" },
  { key: "next", label: "What's the focus next week?", hint: "The specific skill or step you'll work on next.", tag: "PLAN FOR NEXT WEEK" },
];

export function buildGuidedRaw(answers, planned, lastPlan) {
  const parts = [];
  if (planned.length) parts.push("PLANNED INTERVENTIONS DELIVERED THIS WEEK (from the weekly plan):\n" + planned.map((p) => `- ${p}`).join("\n"));
  if (lastPlan) parts.push(`LAST WEEK'S PLAN FOR THIS WEEK WAS: ${lastPlan}`);
  GUIDED_QUESTIONS.forEach((q) => { if ((answers[q.key] || "").trim()) parts.push(`${q.tag}:\n${answers[q.key].trim()}`); });
  return parts.join("\n\n");
}

export default function GuidedNoteInput({ consumerId, answers, setAnswers, planned, setPlanned, onLastNote, disabled }) {
  const [plan, setPlan] = useState(null);
  const [last, setLast] = useState(null);

  useEffect(() => {
    setPlan(null); setLast(null); setPlanned([]);
    onLastNote?.(null);
    if (!consumerId) return;
    api.get("/weekly-plans", { params: { consumer_id: consumerId } }).then(({ data }) => setPlan(data.plan)).catch(() => {});
    api.get("/notes/last", { params: { consumer_id: consumerId } }).then(({ data }) => { setLast(data.last); onLastNote?.(data.last); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerId]);

  const toggle = (iv) => setPlanned((p) => (p.includes(iv) ? p.filter((x) => x !== iv) : [...p, iv]));
  const items = plan?.items || [];

  return (
    <div data-testid="guided-input" className="space-y-5">
      {last && (last.plan || last.interventions) && (
        <div data-testid="last-week-card" className="rounded-xl border border-[#E8DCC4] bg-[#FBF7EE] p-4">
          <p className="text-[10px] font-bold text-[#8a6d1f] uppercase tracking-wider flex items-center gap-1.5"><History className="w-3.5 h-3.5" strokeWidth={2} /> Last note · {last.date_of_service || "earlier"}</p>
          {last.plan && <p className="text-sm text-[#121814] mt-1"><span className="font-semibold">You planned:</span> {last.plan}</p>}
          {last.interventions && <p className="text-xs text-[#7A8C82] mt-1 line-clamp-2"><span className="font-semibold">Last week you did:</span> {last.interventions}</p>}
          <p className="text-[11px] text-[#8a6d1f] mt-1.5">Did you do it? This week's interventions must be different from last week's.</p>
        </div>
      )}

      {items.length > 0 && (
        <div data-testid="planned-chips">
          <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider flex items-center gap-1.5 mb-2"><Target className="w-3.5 h-3.5" strokeWidth={2} /> Tap the planned interventions you delivered this week</p>
          <div className="flex flex-wrap gap-2">
            {items.flatMap((it, gi) => it.interventions.map((iv, ii) => {
              const on = planned.includes(iv);
              return (
                <button key={`${gi}-${ii}`} type="button" data-testid={`planned-chip-${gi}-${ii}`} onClick={() => toggle(iv)}
                  className={`text-left text-xs px-3 py-2 rounded-xl border flex items-start gap-2 max-w-full transition-colors ${on ? "bg-[#EAF1EC] border-[#4A6B53] text-[#121814]" : "bg-white border-[#E2DFD8] text-[#3A453F] hover:border-[#4A6B53]"}`}>
                  {on ? <CheckSquare className="w-4 h-4 shrink-0 text-[#4A6B53]" strokeWidth={1.5} /> : <Square className="w-4 h-4 shrink-0 text-[#B8C4BC]" strokeWidth={1.5} />}
                  <span>{iv}</span>
                </button>
              );
            }))}
          </div>
        </div>
      )}

      {GUIDED_QUESTIONS.map((q, i) => (
        <div key={q.key} data-testid={`guided-q-${q.key}`} className="rounded-xl border border-[#E2DFD8] bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-wider">Question {i + 1} of 3</p>
              <p className="text-base font-semibold text-[#121814] mt-0.5">{q.label}</p>
              <p className="text-xs text-[#7A8C82] mt-0.5">{q.hint}</p>
            </div>
            <FieldDictate size="lg" testid={`guided-dictate-${q.key}`} onText={(t) => setAnswers((a) => ({ ...a, [q.key]: a[q.key] ? a[q.key] + " " + t : t }))} title={`Dictate: ${q.label}`} />
          </div>
          <Textarea data-testid={`guided-input-${q.key}`} value={answers[q.key] || ""} disabled={disabled} rows={3}
            onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
            placeholder="Tap the mic or type…" className="mt-3 bg-[#F9F9F6] border-[#E2DFD8] text-base leading-relaxed resize-y" />
        </div>
      ))}
    </div>
  );
}
